import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Store, Plus, Edit, Tag, Phone, Clock, DollarSign, MapPin, Trash2, Eye, EyeOff } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export default function Merchants({ user, logout }) {
  const [merchantProfile, setMerchantProfile] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  
  // Profile form
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [address, setAddress] = useState("");
  const [website, setWebsite] = useState("");
  const [businessHours, setBusinessHours] = useState("");
  const [category, setCategory] = useState("");

  // Location form
  const [locationName, setLocationName] = useState("");
  const [locationAddress, setLocationAddress] = useState("");
  const [locationPin, setLocationPin] = useState("");
  const [showPin, setShowPin] = useState(false);

  // Discount form
  const [discountTitle, setDiscountTitle] = useState("");
  const [discountDesc, setDiscountDesc] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountPercentage, setDiscountPercentage] = useState("");
  const [maxUses, setMaxUses] = useState("1");
  const [validUntil, setValidUntil] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [discountedPrice, setDiscountedPrice] = useState("");
  const [shopHours, setShopHours] = useState("");
  const [discountContactPhone, setDiscountContactPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [applicableDays, setApplicableDays] = useState([]);
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [memberTypes, setMemberTypes] = useState([]);

  useEffect(() => {
    fetchMerchantProfile();
  }, []);

  const fetchMerchantProfile = async () => {
    try {
      const response = await axios.get(`${API}/merchants/my-profile`, {
        withCredentials: true
      });
      
      if (response.data) {
        setMerchantProfile(response.data);
        loadProfileForm(response.data);
        fetchDiscounts(response.data.id);
        fetchLocations();
      }
    } catch (error) {
      console.error("Error fetching merchant profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadProfileForm = (profile) => {
    setBusinessName(profile.business_name || "");
    setDescription(profile.description || "");
    setContactEmail(profile.contact_email || "");
    setContactPhone(profile.contact_phone || "");
    setAddress(profile.address || "");
    setWebsite(profile.website || "");
    setBusinessHours(profile.business_hours || "");
    setCategory(profile.category || "");
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/merchants/my-locations`, {
        withCredentials: true
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const resetLocationForm = () => {
    setLocationName("");
    setLocationAddress("");
    setLocationPin("");
    setShowPin(false);
    setEditingLocation(null);
  };

  const handleSaveLocation = async () => {
    if (!locationName || !locationPin) {
      toast.error("Name and PIN are required");
      return;
    }

    if (!/^\d{4,6}$/.test(locationPin)) {
      toast.error("PIN must be 4-6 digits");
      return;
    }

    try {
      if (editingLocation) {
        await axios.put(`${API}/merchants/locations/${editingLocation.id}`, {
          name: locationName,
          address: locationAddress || null,
          pin: locationPin
        }, { withCredentials: true });
        toast.success("Location updated");
      } else {
        await axios.post(`${API}/merchants/locations`, {
          name: locationName,
          address: locationAddress || null,
          pin: locationPin
        }, { withCredentials: true });
        toast.success("Location created");
      }
      setShowLocationDialog(false);
      resetLocationForm();
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error(error.response?.data?.detail || "Failed to save location");
    }
  };

  const handleDeleteLocation = async (locationId) => {
    if (!window.confirm("Delete this location? Staff will no longer be able to login with this location's PIN.")) return;
    
    try {
      await axios.delete(`${API}/merchants/locations/${locationId}`, {
        withCredentials: true
      });
      toast.success("Location deleted");
      fetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error("Failed to delete location");
    }
  };

  const handleToggleLocation = async (locationId) => {
    try {
      await axios.put(`${API}/merchants/locations/${locationId}/toggle`, {}, {
        withCredentials: true
      });
      fetchLocations();
    } catch (error) {
      console.error("Error toggling location:", error);
      toast.error("Failed to update location");
    }
  };

  const openEditLocation = (location) => {
    setEditingLocation(location);
    setLocationName(location.name);
    setLocationAddress(location.address || "");
    setLocationPin(""); // Don't show existing PIN
    setShowLocationDialog(true);
  };

  const fetchDiscounts = async (merchantId) => {
    try {
      const response = await axios.get(`${API}/merchants/${merchantId}/discounts`);
      setDiscounts(response.data);
    } catch (error) {
      console.error("Error fetching discounts:", error);
    }
  };

  const saveProfile = async () => {
    if (!businessName || !description) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const payload = {
        business_name: businessName,
        description,
        contact_email: contactEmail,
        contact_phone: contactPhone,
        address,
        website,
        business_hours: businessHours,
        category
      };

      if (merchantProfile) {
        await axios.put(`${API}/merchants/${merchantProfile.id}`, payload, {
          withCredentials: true
        });
        toast.success("Profile updated successfully!");
      } else {
        await axios.post(`${API}/merchants`, payload, {
          withCredentials: true
        });
        toast.success("Profile created successfully!");
      }

      setShowProfileDialog(false);
      fetchMerchantProfile();
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error(error.response?.data?.detail || "Failed to save profile");
    }
  };

  const createDiscount = async () => {
    if (!merchantProfile) {
      toast.error("Please create merchant profile first");
      return;
    }

    if (!discountTitle || !discountCode || !discountPercentage) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const payload = {
        merchant_id: merchantProfile.id,
        title: discountTitle,
        description: discountDesc,
        discount_code: discountCode,
        discount_percentage: parseInt(discountPercentage),
        max_uses_per_member: parseInt(maxUses),
        valid_until: validUntil || null,
        product_url: productUrl || null,
        image_url: imageUrl || null,
        original_price: originalPrice ? parseFloat(originalPrice) : null,
        discounted_price: discountedPrice ? parseFloat(discountedPrice) : null,
        shop_hours: shopHours || null,
        contact_phone: discountContactPhone || null,
        notes: notes || null,
        applicable_days: applicableDays.length > 0 ? applicableDays : null,
        time_start: timeStart || null,
        time_end: timeEnd || null,
        member_types: memberTypes.length > 0 ? memberTypes : null
      };

      await axios.post(`${API}/discounts`, payload, {
        withCredentials: true
      });

      toast.success("Discount created successfully!");
      setShowDiscountDialog(false);
      resetDiscountForm();
      fetchDiscounts(merchantProfile.id);
    } catch (error) {
      console.error("Error creating discount:", error);
      toast.error(error.response?.data?.detail || "Failed to create discount");
    }
  };

  const resetDiscountForm = () => {
    setDiscountTitle("");
    setDiscountDesc("");
    setDiscountCode("");
    setDiscountPercentage("");
    setMaxUses("1");
    setValidUntil("");
    setProductUrl("");
    setImageUrl("");
    setOriginalPrice("");
    setDiscountedPrice("");
    setShopHours("");
    setDiscountContactPhone("");
    setNotes("");
    setApplicableDays([]);
    setTimeStart("");
    setTimeEnd("");
    setMemberTypes([]);
    setEditingDiscount(null);
  };

  const openEditDiscount = (discount) => {
    setEditingDiscount(discount);
    setDiscountTitle(discount.title || "");
    setDiscountDesc(discount.description || "");
    setDiscountCode(discount.discount_code || "");
    setDiscountPercentage(discount.discount_percentage?.toString() || "");
    setMaxUses(discount.max_uses_per_member?.toString() || "1");
    setValidUntil(discount.valid_until ? discount.valid_until.split("T")[0] : "");
    setProductUrl(discount.product_url || "");
    setImageUrl(discount.image_url || "");
    setOriginalPrice(discount.original_price?.toString() || "");
    setDiscountedPrice(discount.discounted_price?.toString() || "");
    setShopHours(discount.shop_hours || "");
    setDiscountContactPhone(discount.contact_phone || "");
    setNotes(discount.notes || "");
    setApplicableDays(discount.applicable_days || []);
    setTimeStart(discount.time_start || "");
    setTimeEnd(discount.time_end || "");
    setMemberTypes(discount.member_types || []);
    setShowDiscountDialog(true);
  };

  const updateDiscount = async () => {
    if (!editingDiscount) return;

    if (!discountTitle || !discountCode || !discountPercentage) {
      toast.error("Please fill required fields");
      return;
    }

    try {
      const payload = {
        merchant_id: merchantProfile.id,
        title: discountTitle,
        description: discountDesc,
        discount_code: discountCode,
        discount_percentage: parseInt(discountPercentage),
        max_uses_per_member: parseInt(maxUses),
        valid_until: validUntil || null,
        product_url: productUrl || null,
        image_url: imageUrl || null,
        original_price: originalPrice ? parseFloat(originalPrice) : null,
        discounted_price: discountedPrice ? parseFloat(discountedPrice) : null,
        shop_hours: shopHours || null,
        contact_phone: discountContactPhone || null,
        notes: notes || null,
        applicable_days: applicableDays.length > 0 ? applicableDays : null,
        time_start: timeStart || null,
        time_end: timeEnd || null,
        member_types: memberTypes.length > 0 ? memberTypes : null
      };

      await axios.put(`${API}/discounts/${editingDiscount.id}`, payload, {
        withCredentials: true
      });

      toast.success("Discount updated successfully!");
      setShowDiscountDialog(false);
      resetDiscountForm();
      fetchDiscounts(merchantProfile.id);
    } catch (error) {
      console.error("Error updating discount:", error);
      toast.error(error.response?.data?.detail || "Failed to update discount");
    }
  };

  const handleSaveDiscount = () => {
    if (editingDiscount) {
      updateDiscount();
    } else {
      createDiscount();
    }
  };

  const toggleDay = (day) => {
    setApplicableDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const toggleMemberType = (type) => {
    setMemberTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-yellow-400 uppercase" style={{ fontFamily: "'Times New Roman', Times, Georgia, serif", letterSpacing: "0.05em" }}>SCC Football</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-4xl font-bold text-white" data-testid="page-title">Merchant Portal</h2>
        </div>

        <Tabs defaultValue="profile" className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="profile" data-testid="profile-tab">
              <Store className="w-4 h-4 mr-2" />
              Business Profile
            </TabsTrigger>
            <TabsTrigger value="locations" data-testid="locations-tab">
              <MapPin className="w-4 h-4 mr-2" />
              Locations
            </TabsTrigger>
            <TabsTrigger value="discounts" data-testid="discounts-tab">
              <Tag className="w-4 h-4 mr-2" />
              My Discounts
            </TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card className="p-8 bg-slate-900/50 shadow-lg">
              {merchantProfile ? (
                <div>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-white mb-2" data-testid="business-name">
                        {merchantProfile.business_name}
                      </h3>
                      {merchantProfile.category && (
                        <span className="inline-block px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm font-semibold">
                          {merchantProfile.category}
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={() => setShowProfileDialog(true)}
                      className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                      data-testid="edit-profile-button"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold text-slate-300 mb-2">Description</h4>
                      <p className="text-slate-400" data-testid="business-description">{merchantProfile.description}</p>
                    </div>

                    {merchantProfile.contact_email && (
                      <div>
                        <h4 className="font-semibold text-slate-300 mb-2">Contact Email</h4>
                        <p className="text-slate-400" data-testid="contact-email">{merchantProfile.contact_email}</p>
                      </div>
                    )}

                    {merchantProfile.contact_phone && (
                      <div>
                        <h4 className="font-semibold text-slate-300 mb-2">Phone</h4>
                        <p className="text-slate-400" data-testid="contact-phone">{merchantProfile.contact_phone}</p>
                      </div>
                    )}

                    {merchantProfile.address && (
                      <div>
                        <h4 className="font-semibold text-slate-300 mb-2">Address</h4>
                        <p className="text-slate-400" data-testid="address">{merchantProfile.address}</p>
                      </div>
                    )}

                    {merchantProfile.website && (
                      <div>
                        <h4 className="font-semibold text-slate-300 mb-2">Website</h4>
                        <a
                          href={merchantProfile.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-yellow-500 hover:underline"
                          data-testid="website"
                        >
                          {merchantProfile.website}
                        </a>
                      </div>
                    )}

                    {merchantProfile.business_hours && (
                      <div>
                        <h4 className="font-semibold text-slate-300 mb-2">Business Hours</h4>
                        <p className="text-slate-400" data-testid="business-hours">{merchantProfile.business_hours}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Store className="w-16 h-16 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">No Business Profile</h3>
                  <p className="text-slate-400 mb-6">Create your business profile to start offering discounts</p>
                  <Button
                    onClick={() => setShowProfileDialog(true)}
                    className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                    data-testid="create-profile-button"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Profile
                  </Button>
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Locations Tab */}
          <TabsContent value="locations">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-white">Scanner Locations</h3>
                  <p className="text-sm text-slate-400 mt-1">
                    Set up locations where staff can scan member discount QR codes
                  </p>
                </div>
                <Button
                  onClick={() => {
                    resetLocationForm();
                    setShowLocationDialog(true);
                  }}
                  className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                  data-testid="add-location-button"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Add Location
                </Button>
              </div>

              {/* Public Scanner URL Info */}
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <h4 className="font-semibold text-yellow-400 mb-2">Public Scanner URL</h4>
                <p className="text-sm text-slate-300 mb-2">
                  Share this URL with your staff to access the scanner without an app account:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-slate-800 rounded text-yellow-300 text-sm">
                    {window.location.origin}/scanner
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/scanner`);
                      toast.success("URL copied!");
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </div>

              {locations.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400 mb-2">No locations set up yet</p>
                  <p className="text-sm text-slate-500">
                    Add a location for each store/outlet where staff will scan QR codes
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {locations.map((location) => (
                    <Card
                      key={location.id}
                      className={`p-4 border ${location.is_active ? 'border-slate-700' : 'border-red-500/30 bg-red-500/5'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-white">{location.name}</h4>
                            {!location.is_active && (
                              <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded">
                                Disabled
                              </span>
                            )}
                          </div>
                          {location.address && (
                            <p className="text-sm text-slate-400">{location.address}</p>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            Created: {new Date(location.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggleLocation(location.id)}
                            className={location.is_active ? "text-amber-400" : "text-emerald-400"}
                          >
                            {location.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditLocation(location)}
                            className="text-slate-400 hover:text-white"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteLocation(location.id)}
                            className="text-red-400 hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts">
            <Card className="p-6 bg-slate-900/50 shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">My Discounts</h3>
                {merchantProfile && (
                  <Button
                    onClick={() => setShowDiscountDialog(true)}
                    className="bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
                    data-testid="create-discount-button"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    Create Discount
                  </Button>
                )}
              </div>

              {!merchantProfile ? (
                <p className="text-center text-slate-400 py-8" data-testid="no-profile-message">
                  Please create your business profile first
                </p>
              ) : discounts.length === 0 ? (
                <p className="text-center text-slate-400 py-8" data-testid="no-discounts-message">
                  No discounts created yet
                </p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {discounts.map((discount) => (
                    <Card
                      key={discount.id}
                      className="p-6 border-2 border-white/5"
                      data-testid={`discount-card-${discount.id}`}
                    >
                      {/* Product Image */}
                      {discount.image_url && (
                        <div className="mb-4 -mx-2 -mt-2">
                          <img 
                            src={discount.image_url} 
                            alt={discount.title}
                            className="w-full h-32 object-cover rounded-t"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      
                      <h4 className="text-lg font-bold text-white mb-2" data-testid={`discount-title-${discount.id}`}>
                        {discount.title}
                      </h4>
                      <p className="text-slate-400 mb-3 text-sm">{discount.description}</p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Discount:</span>
                          <span className="font-semibold text-yellow-500">{discount.discount_percentage}% OFF</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Code:</span>
                          <span className="font-mono font-semibold text-white">{discount.discount_code}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Max Uses:</span>
                          <span className="font-semibold text-white">{discount.max_uses_per_member}</span>
                        </div>
                      </div>

                      {/* Product Link */}
                      {discount.product_url && (
                        <a 
                          href={discount.product_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="block mb-3 text-sm text-yellow-400 hover:text-yellow-300 hover:underline truncate"
                        >
                          View Product →
                        </a>
                      )}

                      {/* Restrictions */}
                      {(discount.applicable_days || discount.time_start || discount.member_types) && (
                        <div className="bg-[#0a0f1a] p-3 rounded-lg mb-3">
                          <p className="text-xs font-semibold text-slate-300 mb-2">Restrictions:</p>
                          {discount.applicable_days && (
                            <p className="text-xs text-slate-400">
                              Days: {discount.applicable_days.join(", ")}
                            </p>
                          )}
                          {discount.time_start && discount.time_end && (
                            <p className="text-xs text-slate-400">
                              Time: {discount.time_start} - {discount.time_end}
                            </p>
                          )}
                          {discount.member_types && (
                            <p className="text-xs text-slate-400">
                              For: {discount.member_types.join(", ")} members
                            </p>
                          )}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-700">
                        <span className={`text-xs font-semibold px-2 py-1 rounded ${discount.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800/50 text-white'}`}>
                          {discount.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <div className="flex items-center gap-2">
                          {discount.valid_until && (
                            <span className="text-xs text-slate-400">
                              Until: {new Date(discount.valid_until).toLocaleDateString()}
                            </span>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDiscount(discount)}
                            className="h-7 px-2 text-xs border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10"
                            data-testid={`edit-discount-${discount.id}`}
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Location Dialog */}
      <Dialog open={showLocationDialog} onOpenChange={(open) => {
        setShowLocationDialog(open);
        if (!open) resetLocationForm();
      }}>
        <DialogContent className="max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle>{editingLocation ? "Edit" : "Add"} Location</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Location Name *</label>
              <Input
                placeholder="e.g., Main Store, Orchard Branch"
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                data-testid="location-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Address (optional)</label>
              <Input
                placeholder="123 Main Street"
                value={locationAddress}
                onChange={(e) => setLocationAddress(e.target.value)}
                data-testid="location-address-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">
                Scanner PIN * {editingLocation && "(enter new PIN to change)"}
              </label>
              <div className="relative">
                <Input
                  type={showPin ? "text" : "password"}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder="4-6 digit PIN"
                  value={locationPin}
                  onChange={(e) => setLocationPin(e.target.value.replace(/\D/g, ''))}
                  className="pr-10"
                  data-testid="location-pin-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPin(!showPin)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                >
                  {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                Staff will use this PIN to login to the scanner at this location
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveLocation}
                className="bg-yellow-500 hover:bg-yellow-400 text-black"
                disabled={!locationName || locationPin.length < 4}
                data-testid="save-location-button"
              >
                {editingLocation ? "Update" : "Create"} Location
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="profile-dialog">
          <DialogHeader>
            <DialogTitle>{merchantProfile ? "Edit" : "Create"} Business Profile</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Business Name *</label>
              <Input
                placeholder="Your Business Name"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                data-testid="business-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">Description *</label>
              <Textarea
                placeholder="Tell members about your business..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
                data-testid="description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Category</label>
                <Input
                  placeholder="e.g., Restaurant, Retail"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  data-testid="category-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Contact Email</label>
                <Input
                  type="email"
                  placeholder="contact@business.com"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid="contact-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Phone</label>
                <Input
                  placeholder="+65 1234 5678"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Website</label>
                <Input
                  placeholder="https://yourwebsite.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  data-testid="website-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Address</label>
              <Input
                placeholder="123 Business Street, Singapore"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                data-testid="address-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Business Hours</label>
              <Input
                placeholder="Mon-Fri: 9AM-6PM, Sat: 10AM-4PM"
                value={businessHours}
                onChange={(e) => setBusinessHours(e.target.value)}
                data-testid="business-hours-input"
              />
            </div>

            <Button
              onClick={saveProfile}
              className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
              data-testid="save-profile-button"
            >
              {merchantProfile ? "Update" : "Create"} Profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Discount Dialog */}
      <Dialog open={showDiscountDialog} onOpenChange={(open) => {
        setShowDiscountDialog(open);
        if (!open) resetDiscountForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="discount-dialog">
          <DialogHeader>
            <DialogTitle>{editingDiscount ? "Edit Discount" : "Create New Discount"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Discount Title *</label>
              <Input
                placeholder="e.g., 20% Off All Items"
                value={discountTitle}
                onChange={(e) => setDiscountTitle(e.target.value)}
                data-testid="discount-title-input"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Description</label>
              <Textarea
                placeholder="Describe the discount..."
                value={discountDesc}
                onChange={(e) => setDiscountDesc(e.target.value)}
                rows={2}
                data-testid="discount-desc-input"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Discount % *</label>
                <Input
                  type="number"
                  placeholder="20"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(e.target.value)}
                  data-testid="discount-percentage-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Discount Code *</label>
                <Input
                  placeholder="SCC20"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  data-testid="discount-code-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Max Uses</label>
                <Input
                  type="number"
                  value={maxUses}
                  onChange={(e) => setMaxUses(e.target.value)}
                  data-testid="max-uses-input"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-2 block">Valid Until (Optional)</label>
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
                data-testid="valid-until-input"
              />
            </div>

            {/* Product Links */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-white mb-3">Product Links (Optional)</h4>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Product/Offer URL</label>
                  <Input
                    type="url"
                    placeholder="https://yourstore.com/product"
                    value={productUrl}
                    onChange={(e) => setProductUrl(e.target.value)}
                    data-testid="product-url-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Link to your product or offer page</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Image URL</label>
                  <Input
                    type="url"
                    placeholder="https://yourstore.com/image.jpg"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    data-testid="image-url-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Link to product image (Imgur, your website, etc.)</p>
                  {imageUrl && (
                    <div className="mt-2 p-2 bg-slate-800 rounded">
                      <p className="text-xs text-slate-400 mb-1">Preview:</p>
                      <img 
                        src={imageUrl} 
                        alt="Preview" 
                        className="max-h-32 rounded"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pricing & Contact Info */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-white mb-3">Pricing & Contact (Optional)</h4>
              
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Original Price (RRP)</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="99.90"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    data-testid="original-price-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Price before discount</p>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Discounted Price</label>
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="79.90"
                    value={discountedPrice}
                    onChange={(e) => setDiscountedPrice(e.target.value)}
                    data-testid="discounted-price-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">Price after discount</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Shop Hours</label>
                  <Input
                    type="text"
                    placeholder="11am - 6pm daily"
                    value={shopHours}
                    onChange={(e) => setShopHours(e.target.value)}
                    data-testid="shop-hours-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Contact Phone</label>
                  <Input
                    type="tel"
                    placeholder="+65 9123 4567"
                    value={discountContactPhone}
                    onChange={(e) => setDiscountContactPhone(e.target.value)}
                    data-testid="discount-contact-phone-input"
                  />
                  <p className="text-xs text-slate-400 mt-1">For appointments</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Additional Notes</label>
                <Input
                  type="text"
                  placeholder="Terms & conditions, special instructions, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="notes-input"
                />
                <p className="text-xs text-slate-400 mt-1">Any additional info for members</p>
              </div>
            </div>

            {/* Restrictions */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-white mb-3">Restrictions (Optional)</h4>
              
              <div className="mb-4">
                <label className="text-sm font-medium mb-2 block">Applicable Days</label>
                <div className="grid grid-cols-4 gap-2">
                  {DAYS_OF_WEEK.map((day) => (
                    <div key={day} className="flex items-center space-x-2">
                      <Checkbox
                        id={day}
                        checked={applicableDays.includes(day)}
                        onCheckedChange={() => toggleDay(day)}
                        data-testid={`day-${day}`}
                      />
                      <label htmlFor={day} className="text-sm cursor-pointer">{day.slice(0, 3)}</label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">Leave empty for all days</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Time Start</label>
                  <Input
                    type="time"
                    value={timeStart}
                    onChange={(e) => setTimeStart(e.target.value)}
                    data-testid="time-start-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Time End</label>
                  <Input
                    type="time"
                    value={timeEnd}
                    onChange={(e) => setTimeEnd(e.target.value)}
                    data-testid="time-end-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Member Types</label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="section"
                      checked={memberTypes.includes("section")}
                      onCheckedChange={() => toggleMemberType("section")}
                      data-testid="member-type-section"
                    />
                    <label htmlFor="section" className="text-sm cursor-pointer">Section Members Only</label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="non-section"
                      checked={memberTypes.includes("non-section")}
                      onCheckedChange={() => toggleMemberType("non-section")}
                      data-testid="member-type-non-section"
                    />
                    <label htmlFor="non-section" className="text-sm cursor-pointer">Non-Section Members</label>
                  </div>
                </div>
                <p className="text-xs text-slate-400 mt-1">Leave empty for all member types</p>
              </div>
            </div>

            <Button
              onClick={handleSaveDiscount}
              className="w-full bg-yellow-500/100 hover:bg-yellow-400 text-black font-semibold"
              data-testid="submit-discount-button"
            >
              {editingDiscount ? "Update Discount" : "Create Discount"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
